import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import AdminLayout from './components/admin/AdminLayout';
import SellerLayout from './components/seller/SellerLayout';
import RoleList from './components/admin/RoleList';
import AddRole from './components/admin/AddRole';
import Home from './pages/Home';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PendingApproval from './pages/PendingApproval';
import Admin from './pages/Admin';
import AdminDashboard from './components/admin/AdminDashboard';
import UserCRUD from './components/admin/UserCRUD';
import UserApproval from './components/admin/UserApproval';
import AdminProfile from './components/admin/AdminProfile';
import OrderManagement from './components/admin/OrderManagement';
import AdminDesignCaseManagement from './components/admin/AdminDesignCaseManagement';
import AdminFinancialCaseManagement from './components/admin/AdminFinancialCaseManagement';

// Import designer components
import DesignerLayout from './components/designer/DesignerLayout';
import DesignerDashboard from './components/designer/DesignerDashboard';
import DesignerCaseList from './components/designer/DesignerCaseList';

// Import seller components
import SellerDashboard from './components/seller/SellerDashboard';
import SellerOrders from './components/seller/SellerOrders';
import SellerStore from './components/seller/SellerStore';
import SellerProfile from './components/seller/SellerProfile';
import CreateDesignCase from './components/seller/CreateDesignCase';
import DesignCaseList from './components/seller/DesignCaseList';
import DesignCaseNotifications from './components/seller/DesignCaseNotifications';
import SellerFinance from './components/seller/SellerFinance';
import SellerResources from './components/seller/SellerResources';
import WebsiteBuilder from './components/seller/WebsiteBuilder';
import DomainManagement from './components/seller/DomainManagement';

                          // Import accountant components
             import AccountantLayout from './components/accountant/AccountantLayout';
             import AccountantFinance from './components/accountant/AccountantFinance';
             import AccountantStatistics from './components/accountant/AccountantStatistics';
             import AccountantProfile from './components/accountant/AccountantProfile';
             import AccountantAddFinance from './components/accountant/AccountantAddFinance';
             import SuperAccountantCreateCase from './components/accountant/SuperAccountantCreateCase';

// Import Work Case Management
import WorkCaseManagement from './components/WorkCaseManagement';
import WorkCaseManagementInner from './components/workcase/WorkCaseManagement';
import WorkCaseStatistics from './components/workcase/WorkCaseStatistics';

// Google OAuth Client ID - bạn cần thay thế bằng Client ID thật
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "496167571958-kcc5s2f78ijg4shjpjab242rr5214an8.apps.googleusercontent.com";

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <div className="App">
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#4ade80',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          
          {/* Protected routes */}
          <Route path="/dashboard" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          {/* Redirect old aggregate page to Assigned list */}
          <Route path="/work-cases" element={<PrivateRoute><Navigate to="/work-cases/assigned" replace /></PrivateRoute>} />
          {/* New nested routes for work cases */}
          <Route path="/work-cases/stats" element={<PrivateRoute><Layout><WorkCaseStatistics viewMode='assigned' /></Layout></PrivateRoute>} />
          <Route path="/work-cases/assigned" element={<PrivateRoute><Layout><WorkCaseManagementInner viewMode='assigned' /></Layout></PrivateRoute>} />
          <Route path="/work-cases/created" element={<PrivateRoute><Layout><WorkCaseManagementInner viewMode='created' /></Layout></PrivateRoute>} />
          
          {/* Seller routes with SellerLayout */}
          <Route path="/seller" element={<PrivateRoute><SellerLayout /></PrivateRoute>}>
            <Route index element={<SellerDashboard />} />
            <Route path="orders" element={<SellerOrders />} />
            <Route path="products" element={<SellerStore />} />
            <Route path="profile" element={<SellerProfile />} />
            <Route path="design-cases" element={<DesignCaseList />} />
            <Route path="finance" element={<SellerFinance />} />
            <Route path="resources" element={<SellerResources />} />
            <Route path="notifications" element={<DesignCaseNotifications />} />
            <Route path="website-builder" element={<WebsiteBuilder />} />
            <Route path="build-website" element={<DomainManagement />} />
            <Route path="domain-management" element={<DomainManagement />} />
            {/* Seller Work Cases nested routes to keep Seller navbar */}
            <Route path="work-cases" element={<Navigate to="assigned" replace />} />
            <Route path="work-cases/stats" element={<WorkCaseStatistics viewMode='assigned' />} />
            <Route path="work-cases/assigned" element={<WorkCaseManagementInner viewMode='assigned' />} />
            <Route path="work-cases/created" element={<WorkCaseManagementInner viewMode='created' />} />
          </Route>
          
          {/* Designer routes with DesignerLayout */}
          <Route path="/designer" element={<PrivateRoute><DesignerLayout /></PrivateRoute>}>
            <Route index element={<DesignerDashboard />} />
            <Route path="cases" element={<DesignerCaseList />} />
            <Route path="work-cases" element={<WorkCaseManagement />} />
          </Route>
          
          {/* Accountant routes */}
              <Route path="/accountant" element={<PrivateRoute><AccountantLayout /></PrivateRoute>}>
                <Route index element={<AccountantStatistics />} />
                <Route path="finance" element={<AccountantFinance />} />
                <Route path="statistics" element={<AccountantStatistics />} />
                <Route path="add-finance" element={<AccountantAddFinance />} />
                <Route path="create-financial-case" element={<SuperAccountantCreateCase />} />
                <Route path="profile" element={<AccountantProfile />} />
                <Route path="work-cases" element={<WorkCaseManagement />} />
              </Route>
          
          {/* Admin routes */}
          <Route path="/admin" element={<PrivateRoute><AdminLayout /></PrivateRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="orders" element={<OrderManagement />} />
            <Route path="design-cases" element={<AdminDesignCaseManagement />} />
            <Route path="financial-cases" element={<AdminFinancialCaseManagement />} />
            <Route path="work-cases" element={<WorkCaseManagement />} />
            <Route path="users" element={<UserCRUD />} />
            <Route path="users/crud" element={<UserCRUD />} />
            <Route path="users/approval" element={<UserApproval />} />
            <Route path="roles" element={<RoleList />} />
            <Route path="roles/list" element={<RoleList />} />
            <Route path="profile" element={<AdminProfile />} />
            {/* Các route admin khác sẽ được thêm sau */}
          </Route>
        </Routes>
      </div>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App; 
